import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Eye, CheckCircle2, RefreshCw, Pencil, Trash2, XCircle, Loader2, FolderInput, FileText, Film, Share2, CalendarClock, Rocket, Clock } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useLang, BLOG_INFO } from "../../context/LangContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import BlogBadge from "../../components/BlogBadge";
import PreviewContent from "../../components/PreviewContent";

const TYPE_ICON = { article: FileText, video: Film, social_post: Share2 };
const STATUS_STYLE = {
  generating: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  draft: "bg-slate-500/15 text-slate-300 border-slate-500/40",
  approved: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  published: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  rejected: "bg-red-500/15 text-red-300 border-red-500/40",
  failed: "bg-red-500/15 text-red-300 border-red-500/40",
};

function defaultSchedule() {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function DraftsTab() {
  const { isAdmin } = useAuth();
  const { t, lang } = useLang();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fBlog, setFBlog] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null);
  const [edit, setEdit] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  // approve dialog
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveMode, setApproveMode] = useState("now");
  const [scheduleAt, setScheduleAt] = useState(defaultSchedule());
  // regenerate dialog
  const [regenTarget, setRegenTarget] = useState(null);
  const [extraPrompt, setExtraPrompt] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = [];
      if (fBlog !== "all") params.push(`blog=${fBlog}`);
      if (fStatus !== "all") params.push(`status=${fStatus}`);
      const { data } = await api.get(`/drafts${params.length ? "?" + params.join("&") : ""}`);
      setDrafts(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [fBlog, fStatus]);

  useEffect(() => { load(); }, [load]);

  // auto-refresh while any draft is still being generated in the background
  useEffect(() => {
    if (!drafts.some((d) => d.status === "generating")) return;
    const id = setInterval(() => { load(); }, 5000);
    return () => clearInterval(id);
  }, [drafts, load]);

  const statusLabel = (s) => {
    if (s === "approved") return t("agendado", "scheduled");
    if (s === "generating") return t("a gerar…", "generating…");
    if (s === "failed") return t("falhou", "failed");
    return s;
  };

  const act = async (fn, id, okMsg) => {
    setBusy(id);
    try { await fn(); toast.success(okMsg); await load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const reject = (d) => act(() => api.post(`/drafts/${d.id}/reject`), d.id, t("Rejeitado", "Rejected"));
  const del = (d) => act(() => api.delete(`/drafts/${d.id}`), d.id, t("Removido", "Deleted"));
  const move = (d, blog) => act(() => api.patch(`/drafts/${d.id}`, { blog }), d.id, t("Movido", "Moved"));
  const publishNow = (d) => act(() => api.post(`/drafts/${d.id}/approve`, {}), d.id, t("Publicado!", "Published!"));
  const unschedule = (d) => act(() => api.post(`/drafts/${d.id}/unschedule`), d.id, t("Agendamento cancelado", "Schedule cancelled"));

  const confirmApprove = async () => {
    const d = approveTarget;
    setApproveTarget(null);
    if (approveMode === "schedule") {
      const iso = new Date(scheduleAt).toISOString();
      await act(() => api.post(`/drafts/${d.id}/approve`, { scheduled_at: iso }), d.id, t("Publicação agendada", "Publication scheduled"));
    } else {
      await act(() => api.post(`/drafts/${d.id}/approve`, {}), d.id, t("Publicado!", "Published!"));
    }
  };

  const confirmRegen = async () => {
    const d = regenTarget;
    setRegenTarget(null);
    await act(() => api.post(`/drafts/${d.id}/regenerate`, { extra_prompt: extraPrompt }), d.id, t("Regenerado", "Regenerated"));
  };

  const openEdit = (d) => {
    setEdit(d); setEditTitle(d.title);
    setEditBody(d.type === "article" ? (d.content?.body_markdown || "") : JSON.stringify(d.content, null, 2));
  };
  const saveEdit = async () => {
    try {
      let content = edit.content;
      if (edit.type === "article") content = { ...edit.content, body_markdown: editBody };
      else { try { content = JSON.parse(editBody); } catch { toast.error("JSON inválido"); return; } }
      await api.patch(`/drafts/${edit.id}`, { title: editTitle, content });
      toast.success(t("Guardado", "Saved")); setEdit(null); await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const fmt = (s) => new Date(s).toLocaleString(lang === "pt" ? "pt-PT" : "en-GB");

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h2 className="font-heading text-2xl font-bold mr-auto">Drafts</h2>
        <Select value={fBlog} onValueChange={setFBlog}><SelectTrigger data-testid="filter-blog" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("Todos os blogs", "All blogs")}</SelectItem>
            {Object.keys(BLOG_INFO).map((b) => <SelectItem key={b} value={b}>{lang === "pt" ? BLOG_INFO[b].pt : BLOG_INFO[b].en}</SelectItem>)}
          </SelectContent></Select>
        <Select value={fStatus} onValueChange={setFStatus}><SelectTrigger data-testid="filter-status" className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("Todos os estados", "All statuses")}</SelectItem>
            <SelectItem value="generating">{t("A gerar", "Generating")}</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="approved">{t("Agendado", "Scheduled")}</SelectItem>
            <SelectItem value="published">{t("Publicado", "Published")}</SelectItem>
            <SelectItem value="failed">{t("Falhou", "Failed")}</SelectItem>
            <SelectItem value="rejected">{t("Rejeitado", "Rejected")}</SelectItem>
          </SelectContent></Select>
      </div>

      {loading ? (
        <p className="text-muted-foreground">{t("A carregar…", "Loading…")}</p>
      ) : drafts.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-card p-12 text-center text-muted-foreground">{t("Sem rascunhos. Gera conteúdo no separador anterior.", "No drafts. Generate content in the previous tab.")}</div>
      ) : (
        <div className="grid gap-4">
          {drafts.map((d) => {
            const Icon = TYPE_ICON[d.type] || FileText;
            const isBusy = busy === d.id;
            const scheduled = d.status === "approved" && d.scheduled_at;
            return (
              <div key={d.id} data-testid={`draft-card-${d.id}`} className="rounded-2xl border border-white/10 bg-card p-5">
                <div className="flex flex-wrap items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <BlogBadge blog={d.blog} />
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground"><Icon className="h-3.5 w-3.5" />{d.type}</span>
                      <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[d.status] || ""}`}>{statusLabel(d.status)}</span>
                      {scheduled && <span className="inline-flex items-center gap-1 text-xs text-amber-300" data-testid={`schedule-badge-${d.id}`}><CalendarClock className="h-3.5 w-3.5" />{fmt(d.scheduled_at)}</span>}
                    </div>
                    <h3 className="mt-2 font-heading text-lg font-semibold truncate">{d.title}</h3>
                    <p className="text-xs text-muted-foreground mt-1">{d.author_name || "—"} · {fmt(d.updated_at)}</p>
                  </div>
                  {d.content?.images?.hero && <img src={d.content.images.hero} alt="" className="h-16 w-24 object-cover rounded-lg border border-white/10" />}
                  {d.content?.thumbnail && <img src={d.content.thumbnail} alt="" className="h-16 w-24 object-cover rounded-lg border border-white/10" />}
                </div>

                {d.status === "failed" && d.error && (
                  <div data-testid={`draft-error-${d.id}`} className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-200 break-words">
                    <span className="font-semibold">{t("Erro na geração", "Generation error")}: </span>{d.error}
                  </div>
                )}

                {d.status === "generating" ? (
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-2 text-sm text-blue-300"><Loader2 className="h-4 w-4 animate-spin" />{t("A gerar conteúdo em segundo plano… a lista atualiza sozinha.", "Generating content in the background… the list updates automatically.")}</span>
                    <Button data-testid={`delete-btn-${d.id}`} size="sm" variant="ghost" className="gap-1.5 text-destructive hover:text-destructive ml-auto" onClick={() => del(d)} disabled={isBusy}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ) : (
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button data-testid={`preview-btn-${d.id}`} size="sm" variant="secondary" className="gap-1.5" onClick={() => setPreview(d)}><Eye className="h-4 w-4" />{t("Pré-visualizar", "Preview")}</Button>

                  {isAdmin && d.status !== "published" && d.status !== "failed" && !scheduled && (
                    <Button data-testid={`approve-btn-${d.id}`} size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-500" onClick={() => { setApproveTarget(d); setApproveMode("now"); setScheduleAt(defaultSchedule()); }} disabled={isBusy}>
                      {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}{t("Aprovar e Publicar", "Approve & Publish")}
                    </Button>
                  )}
                  {isAdmin && scheduled && (
                    <>
                      <Button data-testid={`publish-now-btn-${d.id}`} size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-500" onClick={() => publishNow(d)} disabled={isBusy}>
                        {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}{t("Publicar agora", "Publish now")}
                      </Button>
                      <Button data-testid={`unschedule-btn-${d.id}`} size="sm" variant="ghost" className="gap-1.5 text-muted-foreground" onClick={() => unschedule(d)} disabled={isBusy}><Clock className="h-4 w-4" />{t("Cancelar agendamento", "Unschedule")}</Button>
                    </>
                  )}

                  <Button data-testid={`regenerate-btn-${d.id}`} size="sm" variant="secondary" className="gap-1.5" onClick={() => { setRegenTarget(d); setExtraPrompt(""); }} disabled={isBusy}>
                    <RefreshCw className="h-4 w-4" />{t("Regenerar", "Regenerate")}
                  </Button>
                  <Button data-testid={`edit-btn-${d.id}`} size="sm" variant="secondary" className="gap-1.5" onClick={() => openEdit(d)}><Pencil className="h-4 w-4" />{t("Editar", "Edit")}</Button>
                  <div className="flex items-center gap-1.5">
                    <FolderInput className="h-4 w-4 text-muted-foreground" />
                    <Select value={d.blog} onValueChange={(v) => move(d, v)}>
                      <SelectTrigger data-testid={`move-select-${d.id}`} className="h-9 w-40"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.keys(BLOG_INFO).map((b) => <SelectItem key={b} value={b}>{lang === "pt" ? BLOG_INFO[b].pt : BLOG_INFO[b].en}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {isAdmin && d.status !== "rejected" && d.status !== "published" && d.status !== "failed" && (
                    <Button data-testid={`reject-btn-${d.id}`} size="sm" variant="ghost" className="gap-1.5 text-muted-foreground" onClick={() => reject(d)} disabled={isBusy}><XCircle className="h-4 w-4" />{t("Rejeitar", "Reject")}</Button>
                  )}
                  <Button data-testid={`delete-btn-${d.id}`} size="sm" variant="ghost" className="gap-1.5 text-destructive hover:text-destructive" onClick={() => del(d)} disabled={isBusy}><Trash2 className="h-4 w-4" /></Button>
                </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Preview dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2">{preview && <BlogBadge blog={preview.blog} />}{preview?.title}</DialogTitle>
            <DialogDescription>{t("Pré-visualização do conteúdo gerado.", "Preview of the generated content.")}</DialogDescription></DialogHeader>
          {preview && <PreviewContent type={preview.type} content={preview.content} />}
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("Editar rascunho", "Edit draft")}</DialogTitle>
            <DialogDescription>{t("Edita manualmente o título e o conteúdo.", "Manually edit the title and content.")}</DialogDescription></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div><Label>{t("Título", "Title")}</Label><Input data-testid="edit-title-input" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="mt-1.5" /></div>
              <div>
                <Label>{edit.type === "article" ? t("Corpo (markdown)", "Body (markdown)") : t("Conteúdo (JSON)", "Content (JSON)")}</Label>
                <Textarea data-testid="edit-body-input" value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={14} className="mt-1.5 font-mono text-xs" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEdit(null)}>{t("Cancelar", "Cancel")}</Button>
            <Button data-testid="save-edit-btn" onClick={saveEdit}>{t("Guardar", "Save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve / Schedule dialog */}
      <Dialog open={!!approveTarget} onOpenChange={(o) => !o && setApproveTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("Aprovar e publicar", "Approve & publish")}</DialogTitle>
            <DialogDescription>{t("Publica agora ou agenda para uma data futura.", "Publish now or schedule for a future date.")}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="approve-mode-now" onClick={() => setApproveMode("now")} className={`rounded-xl border p-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${approveMode === "now" ? "border-emerald-500 bg-emerald-500/10 text-foreground" : "border-white/10 text-muted-foreground"}`}><Rocket className="h-4 w-4" />{t("Publicar agora", "Publish now")}</button>
              <button data-testid="approve-mode-schedule" onClick={() => setApproveMode("schedule")} className={`rounded-xl border p-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${approveMode === "schedule" ? "border-amber-500 bg-amber-500/10 text-foreground" : "border-white/10 text-muted-foreground"}`}><CalendarClock className="h-4 w-4" />{t("Agendar", "Schedule")}</button>
            </div>
            {approveMode === "schedule" && (
              <div>
                <Label>{t("Data e hora de publicação", "Publish date & time")}</Label>
                <Input data-testid="schedule-datetime-input" type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} className="mt-1.5" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setApproveTarget(null)}>{t("Cancelar", "Cancel")}</Button>
            <Button data-testid="confirm-approve-btn" className="bg-emerald-600 hover:bg-emerald-500" onClick={confirmApprove}>{approveMode === "schedule" ? t("Agendar", "Schedule") : t("Publicar", "Publish")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate dialog */}
      <Dialog open={!!regenTarget} onOpenChange={(o) => !o && setRegenTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("Regenerar com IA", "Regenerate with AI")}</DialogTitle>
            <DialogDescription>{t("Adiciona informação extra ou instruções para melhorar o conteúdo (opcional).", "Add extra information or instructions to improve the content (optional).")}</DialogDescription></DialogHeader>
          <div>
            <Label>{t("Informação/prompt adicional", "Additional info/prompt")}</Label>
            <Textarea data-testid="regen-extra-input" value={extraPrompt} onChange={(e) => setExtraPrompt(e.target.value)} rows={4} className="mt-1.5" placeholder={t("ex.: inclui dados de 2026 e um exemplo prático", "e.g. include 2026 data and a practical example")} />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRegenTarget(null)}>{t("Cancelar", "Cancel")}</Button>
            <Button data-testid="confirm-regen-btn" className="gap-1.5" onClick={confirmRegen}><RefreshCw className="h-4 w-4" />{t("Regenerar", "Regenerate")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
